<script setup lang="ts">
import { SellingMethod, AssetType } from '~/apiClient';
import type { Breadcrumb } from '~/types/breadcrumb.type';

// COMPOSABLES
const { tt } = useTypedI18n();
const route = useRoute();
const sellingMethod = useSlpSellingMethod();
const parsedRouteParams = useSlpParsedRouteParamsState(route);
const { brandName, modelName, apiParams } = useSlpParams(sellingMethod);
const { formatSlpRoute } = useSlpRouteUtils();

// DATA
const title = computed<string>(() => {
    const titleParams = [
        parsedRouteParams.value.platformClass ? tt(`asset.class.${parsedRouteParams.value.platformClass}`) : parsedRouteParams.value.platformClass,
        brandName.value,
        modelName.value,
    ].filter(Boolean);

    return titleParams.every(value => !value)
        ? tt(`slp.${sellingMethod.value}.page.title.base`)
        : tt(`slp.${sellingMethod.value}.page.title.filtered`, {
              assetType: tt(`asset.type.${parsedRouteParams.value.assetType!}`),
              filters: titleParams.join(' '),
          });
});

const breadCrumbs = computed<Breadcrumb[]>(() => [
    {
        label: tt('homepage.link'),
        to: 'index',
    },
    {
        label: tt(`asset.type.${AssetType.Bus}`, 2),
        to: formatSlpRoute({ sellingMethod: sellingMethod.value, assetType: AssetType.Bus }),
    },
    ...insertIf(!!parsedRouteParams.value.platformClass, () => ({
        label: tt(`asset.class.${parsedRouteParams.value.platformClass!}`),
        to: formatSlpRoute({
            sellingMethod: sellingMethod.value,
            assetType: AssetType.Bus,
            platformClass: parsedRouteParams.value.platformClass,
        }),
    })),
    ...insertIf(!!brandName.value, {
        label: brandName.value!,
        to: formatSlpRoute({
            ...setIf(!!parsedRouteParams.value.platformClass, { platformClass: parsedRouteParams.value.platformClass }),
            sellingMethod: sellingMethod.value,
            assetType: AssetType.Bus,
            brandUrlKey: apiParams.value.brandUrlKey,
        }),
    }),
    ...insertIf(!!modelName.value, {
        label: modelName.value!,
        to: formatSlpRoute({
            assetType: AssetType.Bus,
            ...setIf(!!parsedRouteParams.value.platformClass, { platformClass: parsedRouteParams.value.platformClass }),
            sellingMethod: sellingMethod.value,
            brandUrlKey: apiParams.value.brandUrlKey,
            modelUrlKey: apiParams.value.modelUrlKey,
        }),
    }),
]);

const ui = computed(() => {
    if (sellingMethod.value === SellingMethod.Auction) {
        return {
            imageSrc: '/block/hero/yellow-bus.webp',
            image: '',
            wrapper: 'bg-primary text-inverted',
        };
    }

    return {
        imageSrc: '/block/hero/blue-bus.webp',
        image: 'scale-x-[-1]',
        wrapper: 'bg-secondary text-highlighted',
    };
});
</script>

<template>
    <section class="relative overflow-x-hidden" :class="ui.wrapper">
        <BaseWidthContainer class="flex h-[300px] flex-col justify-between py-9">
            <BaseBreadcrumb color="inherit" :items="breadCrumbs" />
            <h1 class="text-3xl font-semibold text-inherit sm:w-1/2 md:text-4xl lg:text-5xl">{{ title }}</h1>
        </BaseWidthContainer>
        <img
            :src="ui.imageSrc"
            alt="Fleequid"
            class="absolute top-[37px] hidden h-[227px] w-[860px] sm:right-[-500px] sm:block lg:right-[-300px] xl:right-[-180px]"
            :class="ui.image"
        />
    </section>
</template>
